import { AreaChart, AreaChartSeries, getFilteredChartTooltipPayload } from '@mantine/charts'
import { Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconShieldCheck } from '@tabler/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import Loading from '@/components/Loading'
import VerifyModal from '@/components/Modals/Governance/VerifyModal'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useElectionResults } from '@/hooks/useElectionResults'
import { CandidateStatus } from '@/hooks/useGovernance'
import useL1NS from '@/hooks/useL1NS'
import { useAuth } from '@/plugins/auth'
import { generateColor } from '@/utils/utils'

import ElectionConfidence from '../GovernanceList/ElectionConfidence'
import ResultsAction from '../GovernanceList/ListActions/ResultsAction'
import NomineesList from '../GovernanceList/NomineesList'

interface ChartTooltipProps {
  label: string
  payload: Record<string, any>[] | undefined
  usernames?: AreaChartSeries[]
}

function ChartTooltip({ label, payload, usernames }: ChartTooltipProps) {
  const theme = useMantineTheme()
  if (!payload) return null

  return (
    <Paper
      px="md"
      py="sm"
      withBorder
      shadow="md"
      radius="md"
      style={{
        backgroundColor: theme.colors.background2[0],
        borderColor: theme.colors.popUpOutline[0],
      }}
    >
      <Text fw={500} mb={5}>
        {label}
      </Text>
      {getFilteredChartTooltipPayload(payload)
        .sort((a, b) => b.value - a.value)
        .map((item: any) => {
          const username = usernames?.find(user => user.name === item.name)?.label || item.name
          return (
            <Text key={item.name} c={item.color}>
              {username}: {new Intl.NumberFormat('en-US').format(item.value)}
            </Text>
          )
        })}
    </Paper>
  )
}

interface ResultsProps {}

const Results: React.FC<ResultsProps> = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const [opened, { open, close }] = useDisclosure()
  const { address } = useAuth()

  // Governance data
  const { electionData, nominationData } = useGovernanceContext()
  const {
    finalized,
    candidateResults,
    stats,
    numVoters,
    verifiedVoters,
    userTotal,
    userVerified,
    //    isLoading: isLoadingElectionResults, // TODO: Add loading
  } = useElectionResults(electionData.idx)
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const { getL1nsName } = useL1NS()
  const [candidateUsernames, setCandidateUsernames] = useState<AreaChartSeries[]>([])

  useEffect(() => {
    const fetchUsernames = async () => {
      const usernames = await Promise.all(
        candidateResults
          .filter(candidate => candidate.status === 'ACCEPTED')
          .map(async candidate => {
            const username = await getL1nsName(candidate.candidate)
            return {
              name: candidate.candidate,
              label: username || candidate.candidate,
              color: generateColor(candidate.candidate),
            }
          })
      )
      setCandidateUsernames(usernames)
    }

    fetchUsernames()
  }, [candidateResults, getL1nsName])

  const sortedNominationData = useMemo(
    () =>
      candidateResults.map(candidate => ({
        ...(nominationData.find(nomination => nomination.nominee === candidate.candidate) || {
          nominee: candidate.candidate,
          nominator: '',
          owner: '',
          status: 'PENDING' as CandidateStatus,
          cid: '',
        }),
      })),
    [candidateResults, nominationData]
  )

  return (
    <>
      {finalized && candidateResults.length > 0 ? (
        <Stack gap="60px">
          <NomineesList
            candidates={sortedNominationData}
            phase="results"
            title="Election Results"
            subHeader={<ElectionConfidence verifiedVoters={verifiedVoters} numVoters={numVoters} />}
            button={
              <Stack style={{ flexDirection: 'row', gap: '30px', alignItems: 'center' }}>
                {userVerified ? (
                  <Stack style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                    <Title
                      order={5}
                      style={{ color: theme.colors.l1Primary[0], textAlign: 'right' }}
                    >
                      {t('components.governance.results.youAreVerified', 'Your votes are verified')}
                    </Title>

                    {!isMobile && <IconShieldCheck size={16} color={theme.colors.l1Primary[0]} />}
                  </Stack>
                ) : (
                  <>
                    {address && userTotal.value !== 0n && (
                      <RootButton style1={false} onClick={open}>
                        {t('buttons.verify', 'Verify')}
                      </RootButton>
                    )}
                  </>
                )}
              </Stack>
            }
            extraInfo={
              <Stack gap="30px">
                <Title order={4}>{t('components.governance.results.chart', 'Voting Chart')}</Title>
                <AreaChart
                  h="60vh"
                  data={stats}
                  dataKey="date"
                  series={candidateUsernames}
                  tooltipProps={{
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content: ({ label, payload }) => (
                      <ChartTooltip
                        label={label}
                        payload={payload}
                        usernames={candidateUsernames}
                      />
                    ),
                  }}
                  withLegend
                  legendProps={{
                    verticalAlign: 'bottom',
                    height: 120,
                    wrapperStyle: { color: theme.colors.textAndIcon[0] },
                  }}
                  curveType="natural"
                  tickLine="x"
                  xAxisLabel="Date"
                  yAxisLabel="Votes"
                />
              </Stack>
            }
            action={ResultsAction}
            actionProps={{ status: 'REMOVED' }}
          />
        </Stack>
      ) : (
        <Loading title="Election Results are being calculated..." />
      )}
      <ModalSkeleton title="Verify Votes" opened={opened} onClose={close} size="lg">
        <VerifyModal close={close} />
      </ModalSkeleton>
    </>
  )
}

export default Results
